import { Dialog as DialogPrimitive } from "radix-ui";
import { Link } from "@tanstack/react-router";
import { MenuItem } from "../../data/menuItems";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
}

function flattenMenuItems(items: MenuItem[]): MenuItem[] {
  return items.reduce<MenuItem[]>((acc, item) => {
    if (item.children) {
      return acc.concat(item.children);
    } else {
      return acc.concat(item);
    }
  }, []);
}

export function MobileMenu({ isOpen, onClose, menuItems }: MobileMenuProps) {
  const flatMenuItems = flattenMenuItems(menuItems);
  const linkClassName =
    "rounded-lg p-3 text-right text-lg font-semibold leading-6 text-white/80 hover:bg-darkdarkred hover:text-white transition-all duration-200 w-full block";

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        {/* Backdrop overlay */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity duration-300 data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
        />

        <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
          <DialogPrimitive.Content
            className="pointer-events-auto relative w-full max-w-sm transform bg-linear-to-b from-darkdarkred to-darkred p-6 shadow-2xl transition duration-300 ease-in-out data-[state=closed]:translate-x-full data-[state=open]:translate-x-0 flex flex-col h-full overflow-y-auto outline-none"
          >
            <DialogPrimitive.Title className="sr-only">
              Navigation Menu
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              Mobile navigation links
            </DialogPrimitive.Description>

            {/* Header row in mobile menu */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <Link to="/" onClick={onClose} className="focus:outline-none">
                <span className="sr-only">EVTCC</span>
                <img
                  className="h-14 w-auto drop-shadow-md active:scale-95 transition-transform"
                  src="/evtcc-logo.png"
                  alt="EVTCC Logo"
                />
              </Link>

              {/* Close Button: An X-morph styling button */}
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  className="relative inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-white/10 focus:outline-none transition-colors duration-200 cursor-pointer h-12 w-12 border border-white/20"
                >
                  <span className="sr-only">Close main menu</span>
                  <div className="relative flex flex-col justify-center items-center w-8 h-8">
                    <span className="block absolute h-1 w-7 rounded-full bg-white rotate-45" />
                    <span className="block absolute h-1 w-7 rounded-full bg-white -rotate-45" />
                  </div>
                </button>
              </DialogPrimitive.Close>
            </div>

            {/* Navigation Links list */}
            <div className="mt-6 flex-1">
              <nav className="flex flex-col space-y-1">
                {flatMenuItems.map((item) => {
                  if (item.type === "icon") {
                    const classNames = "flex items-center justify-end rounded-lg p-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200";
                    const content = (
                      <>
                        <span className="mr-3 text-lg font-semibold">{item.name}</span>
                        <img
                          src={item.img}
                          className="size-7 object-contain drop-shadow"
                          alt={item.name}
                        />
                      </>
                    );

                    return item.external ? (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classNames}
                        onClick={onClose}
                      >
                        {content}
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href || "/"}
                        className={classNames}
                        activeProps={{ className: "text-white bg-white/10 font-bold" }}
                        onClick={onClose}
                      >
                        {content}
                      </Link>
                    );
                  }

                  if (item.external) {
                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                        onClick={onClose}
                      >
                        {item.name}
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.href || "/"}
                      className={linkClassName}
                      activeProps={{ className: "text-white bg-white/10 font-bold" }}
                      onClick={onClose}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}